import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  FaUser,
  FaCalendarAlt,
  FaTag,
  FaVideo,
  FaImage,
  FaFileAlt,
  FaMusic,
  FaNewspaper,
  FaChartBar,
  FaBullhorn,
  FaPenAlt,
  FaEye,
  FaHeart,
  FaThumbsDown,
  FaShareAlt,
  FaSearch,
  FaStar,
  FaPlayCircle,
  FaHeadphones,
  FaDownload,
  FaExternalLinkAlt,
  FaBookOpen,
  FaExclamationCircle,
  FaTimes,
  FaPlay,
  FaClock,
  FaArrowLeft,
  FaFilter,
  FaChevronDown,
  FaLayerGroup,
  FaFire,
  FaTrendingUp,
  FaRegClock,
  FaEllipsisV,
  FaPlus,
  FaMinus,
  FaExpand,
  FaCompress,
  FaThLarge,
  FaList,
  FaBorderAll,
  FaSquare,
  FaAngleDoubleLeft,
  FaMosque,
  FaPray,
  FaQuran
} from 'react-icons/fa';

const Home = () => {
  const [content, setContent] = useState([]);
  const [contentWithTokens, setContentWithTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredContent, setFilteredContent] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');

  // Filter content based on search and category
  useEffect(() => {
    let filtered = contentWithTokens;
    
    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(item => 
        item.title_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Apply sorting
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (sortBy === 'popular') {
      filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    
    setFilteredContent(filtered);
  }, [searchTerm, selectedCategory, contentWithTokens, sortBy]);

  // Extract unique categories
  useEffect(() => {
    if (content.length > 0) {
      const uniqueCategories = ['all', ...new Set(content.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories);
    }
  }, [content]);

  // Generate Cloudinary thumbnail URL for video
  const generateCloudinaryThumbnail = (videoUrl, options = {}) => {
    if (!videoUrl) return null;
    
    const url = new URL(videoUrl);
    const pathParts = url.pathname.split('/');
    const uploadIndex = pathParts.indexOf('upload');
    
    if (uploadIndex === -1) return videoUrl;
    
    const defaultOptions = {
      width: 400,
      height: 225,
      crop: 'fill',
      quality: 'auto:good',
      format: 'jpg'
    };
    
    const thumbOptions = { ...defaultOptions, ...options };
    
    const transformations = [
      `c_${thumbOptions.crop}`,
      `w_${thumbOptions.width}`,
      `h_${thumbOptions.height}`,
      `q_${thumbOptions.quality}`,
      `f_${thumbOptions.format}`
    ].join(',');
    
    return videoUrl.replace('/upload/', `/upload/${transformations}/`);
  };

  const getThumbnailUrl = (item) => {
    if (!item.featured_media) return null;
    
    if (item.content_type === 'video' && item.featured_media.includes('cloudinary.com')) {
      return generateCloudinaryThumbnail(item.featured_media);
    }
    
    if (item.content_type === 'image') {
      return item.featured_media;
    }
    
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'الآن';
    } else if (diffInHours < 24) {
      return `منذ ${diffInHours} ساعة`;
    } else {
      return date.toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const getContentTypeIcon = (contentType) => {
    const icons = {
      'video': <FaVideo />,
      'image': <FaImage />,
      'document': <FaFileAlt />,
      'audio': <FaMusic />,
      'news': <FaNewspaper />,
      'report': <FaChartBar />,
      'statement': <FaBullhorn />,
      'article': <FaPenAlt />,
      'religious': <FaMosque />,
      'prayer': <FaPray />,
      'quran': <FaQuran />,
      'default': <FaBookOpen />
    };
    return icons[contentType] || icons.default;
  };

  const getContentTypeText = (contentType) => {
    const types = {
      'video': 'فيديو',
      'image': 'صورة',
      'document': 'وثيقة',
      'audio': 'صوتي',
      'news': 'خبر',
      'report': 'تقرير',
      'statement': 'بيان',
      'article': 'مقال',
      'religious': 'ديني',
      'prayer': 'أذكار',
      'quran': 'قرآن',
      'default': 'محتوى'
    };
    return types[contentType] || types.default;
  };

  const getActionButtonProps = (contentType) => {
    switch (contentType) {
      case 'video':
        return { text: 'مشاهدة', icon: <FaPlayCircle /> };
      case 'audio':
        return { text: 'استماع', icon: <FaHeadphones /> };
      case 'document':
        return { text: 'تحميل', icon: <FaDownload /> };
      default:
        return { text: 'عرض التفاصيل', icon: <FaExternalLinkAlt /> };
    }
  };

  const fetchTokensForContent = async (contentItems) => {
    try {
      const contentIds = contentItems.map(item => item.id);
      
      const { data: tokensData, error } = await supabase
        .from('content_tokens')
        .select('token, content_id')
        .in('content_id', contentIds);

      if (error) throw error;

      const tokenMap = {};
      if (tokensData) {
        tokensData.forEach(token => {
          tokenMap[token.content_id] = token.token;
        });
      }

      return contentItems.map(item => ({
        ...item,
        content_token: tokenMap[item.id] || null
      }));
    } catch (error) {
      console.error('Error fetching tokens:', error);
      return contentItems.map(item => ({
        ...item,
        content_token: null
      }));
    }
  };

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch only image content
      const { data: contentData, error } = await supabase
        .from('content')
        .select('*')
        .eq('status', 'published')
        .eq('content_type', 'image') // Only fetch image content
        .order('created_at', { ascending: false });

      if (error) throw error;

      const contentItems = contentData || [];
      setContent(contentItems);
      
      const contentWithTokens = await fetchTokensForContent(contentItems);
      setContentWithTokens(contentWithTokens);
      setFilteredContent(contentWithTokens);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();

    const subscription = supabase
      .channel('content_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'content' },
        (payload) => {
          // Only add if it's image content
          if (payload.new.content_type === 'image') {
            fetchContent();
          }
        }
      )
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'content' },
        (payload) => {
          // Only update if it's image content
          if (payload.new.content_type === 'image') {
            fetchContent();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [fetchContent]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSortBy('newest');
  };

  const getFallbackIcon = (contentType) => {
    switch (contentType) {
      case 'video':
        return '🎬';
      case 'image':
        return '🖼️';
      case 'audio':
        return '🎵';
      case 'document':
        return '📄';
      case 'religious':
        return '🕌';
      case 'prayer':
        return '🤲';
      case 'quran':
        return '📖';
      default:
        return '📎';
    }
  };

  const getContentTypeGradient = (contentType) => {
    switch (contentType) {
      case 'video':
        return 'linear-gradient(145deg, #3b82f6, #2563eb)';
      case 'image':
        return 'linear-gradient(145deg, #10b981, #059669)';
      case 'audio':
        return 'linear-gradient(145deg, #8b5cf6, #7c3aed)';
      case 'document':
        return 'linear-gradient(145deg, #f59e0b, #d97706)';
      case 'news':
        return 'linear-gradient(145deg, #ef4444, #dc2626)';
      case 'report':
        return 'linear-gradient(145deg, #6366f1, #4f46e5)';
      case 'statement':
        return 'linear-gradient(145deg, #ec4899, #db2777)';
      case 'article':
        return 'linear-gradient(145deg, #14b8a6, #0d9488)';
      case 'religious':
        return 'linear-gradient(145deg, #065f46, #047857)';
      case 'prayer':
        return 'linear-gradient(145deg, #b45309, #d97706)';
      case 'quran':
        return 'linear-gradient(145deg, #7e22ce, #9333ea)';
      default:
        return 'linear-gradient(145deg, #255853, #1e4743)';
    }
  };

  // Render different card layouts based on viewMode
  const renderCard = (item, index) => {
    const actionProps = getActionButtonProps(item.content_type);
    const thumbnailUrl = getThumbnailUrl(item);
    const isVideo = item.content_type === 'video';
    const gradientBg = getContentTypeGradient(item.content_type);
    
    if (viewMode === 'list') {
      return (
        <div key={item.id} className="content-card-list" style={{animationDelay: `${index * 0.05}s`}}>
          <div className="card-list-image" style={{ background: gradientBg }}>
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt={item.title_ar || item.title}
                className="card-list-img"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="no-image-list">${getFallbackIcon(item.content_type)}</div>
                  `;
                }}
              />
            ) : (
              <div className="no-image-list">{getFallbackIcon(item.content_type)}</div>
            )}
            {(isVideo || item.content_type === 'audio') && (
              <div className="list-play-icon">
                {isVideo ? <FaPlay size={16} /> : <FaHeadphones size={16} />}
              </div>
            )}
          </div>
          
          <div className="card-list-content">
            <div className="card-list-header">
              <h3 className="card-list-title">{item.title_ar || item.title}</h3>
              <div className="card-list-type">
                {getContentTypeIcon(item.content_type)}
                <span>{getContentTypeText(item.content_type)}</span>
              </div>
            </div>
            
            {item.description && (
              <p className="card-list-description">
                {item.description_ar || item.description}
              </p>
            )}
            
            <div className="card-list-meta">
              <div className="card-list-meta-item">
                <FaUser />
                <span>{item.author_ar || item.author || 'مؤسسة إرضاء الإعلامية'}</span>
              </div>
              <div className="card-list-meta-item">
                <FaRegClock />
                <span>{formatDate(item.created_at)}</span>
              </div>
              <div className="card-list-meta-item">
                <FaTag />
                <span>{item.category || 'عام'}</span>
              </div>
            </div>
            
            <div className="card-list-footer">
            
              
              <Link 
                to={`/content/${item.content_token || item.id}`}
                className="card-list-button"
              >
                {actionProps.icon}
                {actionProps.text}
                <FaAngleDoubleLeft />
              </Link>
            </div>
          </div>
          
          {(item.is_breaking || item.is_featured) && (
            <div className="card-list-badges">
              {item.is_breaking && (
                <div className="badge-list breaking">
                  <FaExclamationCircle />
                  <span>عاجل</span>
                </div>
              )}
              {item.is_featured && (
                <div className="badge-list featured">
                  <FaStar />
                  <span>مميز</span>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    if (viewMode === 'compact') {
      return (
        <div key={item.id} className="content-card-compact" style={{animationDelay: `${index * 0.05}s`}}>
          <div className="compact-image" style={{ background: gradientBg }}>
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl} 
                alt={item.title_ar || item.title}
                className="compact-img"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="no-image-compact">${getFallbackIcon(item.content_type)}</div>
                  `;
                }}
              />
            ) : (
              <div className="no-image-compact">{getFallbackIcon(item.content_type)}</div>
            )}
          </div>
          
          <div className="compact-content">
            <div className="compact-header">
              <h3 className="compact-title">{item.title_ar || item.title}</h3>
              <div className="compact-type">
                {getContentTypeIcon(item.content_type)}
              </div>
            </div>
            
            <div className="compact-meta">
              <span>{formatDate(item.created_at)}</span>
              <span>•</span>
              <span>{item.category || 'عام'}</span>
            </div>
            
            <div className="compact-footer">
              <div className="compact-stats">
                <span><FaEye /> {item.views || 0}</span>
                <span><FaHeart /> {item.likes || 0}</span>
              </div>
              
              <Link 
                to={`/content/${item.content_token || item.id}`}
                className="compact-button"
              >
                <FaArrowLeft />
              </Link>
            </div>
          </div>
          
          {(item.is_breaking || item.is_featured) && (
            <div className="compact-badge">
              {item.is_breaking && <FaExclamationCircle className="breaking-icon" />}
              {item.is_featured && <FaStar className="featured-icon" />}
            </div>
          )}
        </div>
      );
    }
    
    // Default grid view
    return (
      <div key={item.id} className="content-card" style={{animationDelay: `${index * 0.1}s`}}>
        <div className="card-media" style={{ background: gradientBg }}>
          {thumbnailUrl ? (
            <>
              <img 
                src={thumbnailUrl} 
                alt={item.title_ar || item.title}
                className="card-image"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="no-image-media">${getFallbackIcon(item.content_type)}</div>
                  `;
                }}
              />
              {(isVideo || item.content_type === 'audio') && (
                <div className="media-overlay">
                  <div className="play-button">
                    {isVideo ? <FaPlay size={24} /> : <FaHeadphones size={24} />}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-image-media">{getFallbackIcon(item.content_type)}</div>
          )}
          
          <div className="media-badge">
            {getContentTypeIcon(item.content_type)}
            <span>{getContentTypeText(item.content_type)}</span>
          </div>
        </div>
        
        <div className="card-content">
          <div className="card-category">
            <FaTag />
            <span>{item.category || 'عام'}</span>
          </div>
          
          <h3 className="card-title">{item.title_ar || item.title}</h3>
          
          {item.description && (
            <p className="card-description">
              {item.description_ar || item.description}
            </p>
          )}
          
          <div className="card-author">
            <FaUser />
            <span>{item.author_ar || item.author || 'مؤسسة إرضاء الإعلامية'}</span>
            <span className="card-date">
              <FaRegClock />
              {formatDate(item.created_at)}
            </span>
          </div>
          
          <div className="card-stats">
            <div className="stat-group">
              <FaEye />
              <span>{item.views || 0}</span>
            </div>
            <div className="stat-group">
              <FaHeart />
              <span>{item.likes || 0}</span>
            </div>
            <div className="stat-group">
              <FaShareAlt />
              <span>{item.shares || 0}</span>
            </div>
          </div>
          
          <div className="card-actions">
            <Link 
              to={`/content/${item.content_token || item.id}`}
              className="card-button"
            >
              <span>{actionProps.text}</span>
              {actionProps.icon}
            </Link>
            
            {(item.is_breaking || item.is_featured) && (
              <div className="card-badges">
                {item.is_breaking && (
                  <div className="badge-card breaking">
                    <FaExclamationCircle />
                  </div>
                )}
                {item.is_featured && (
                  <div className="badge-card featured">
                    <FaStar />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home">
      <style>{`
        .home {
          background: #f5f7fa;
          min-height: 100vh;
          font-family: 'ITF Qomra Arabic', 'Segoe UI', sans-serif;
          direction: rtl;
          padding: 2rem;
        }

        .main-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        /* Page Title */
        .page-title {
          text-align: center;
          margin-bottom: 2rem;
        }

        .page-title h1 {
          font-size: 2.2rem;
          font-weight: 700;
          color: #255853;
          margin-bottom: 0.5rem;
          position: relative;
          display: inline-block;
        }

        .page-title h1:after {
          content: '';
          position: absolute;
          bottom: -10px;
          right: 50%;
          transform: translateX(50%);
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, #255853, #357E77);
          border-radius: 2px;
        }

        .page-title p {
          color: #64748b;
          font-size: 1rem;
          margin-top: 1rem;
        }

        /* Filters Section */
        .filters-section {
          background: white;
          border-radius: 20px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(37, 88, 83, 0.1);
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          margin-bottom: ${showFilters ? '1.5rem' : '0'};
        }

        .filters-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #255853;
          font-weight: 600;
          font-size: 1rem;
        }

        .filters-toggle {
          color: #255853;
          transition: transform 0.3s ease;
        }

        .filters-toggle.open {
          transform: rotate(180deg);
        }

        .filters-grid {
          display: ${showFilters ? 'grid' : 'none'};
          grid-template-columns: 2fr 1fr auto;
          gap: 1rem;
          align-items: center;
        }

        @media (max-width: 768px) {
          .filters-grid {
            grid-template-columns: 1fr;
          }
        }

        .search-wrapper {
          position: relative;
        }

        .search-icon {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #255853;
          opacity: 0.5;
        }

        .search-input {
          width: 100%;
          padding: 1rem 2.5rem 1rem 1.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          font-size: 0.95rem;
          background: white;
          transition: all 0.3s ease;
          font-family: inherit;
          text-align: right;
        }

        .search-input:focus {
          outline: none;
          border-color: #255853;
          box-shadow: 0 0 0 4px rgba(37, 88, 83, 0.1);
        }

        .filter-group {
          position: relative;
        }

        .filter-select {
          width: 100%;
          padding: 1rem 1.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          font-size: 0.95rem;
          background: white;
          color: #4a5568;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: inherit;
          text-align: right;
          appearance: none;
        }

        .filter-select:focus {
          outline: none;
          border-color: #255853;
        }

        .select-arrow {
          position: absolute;
          left: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #255853;
          pointer-events: none;
        }

        .clear-filters {
          padding: 1rem 1.5rem;
          background: #f1f5f9;
          color: #4a5568;
          border: none;
          border-radius: 16px;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: inherit;
          white-space: nowrap;
        }

        .clear-filters:hover {
          background: #e2e8f0;
          color: #255853;
        }

        /* Results Header */
        .results-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .results-count {
          color: #255853;
          font-size: 0.95rem;
          font-weight: 500;
          background: rgba(37, 88, 83, 0.1);
          padding: 0.5rem 1.25rem;
          border-radius: 40px;
        }

        .view-toggle {
          display: flex;
          gap: 0.5rem;
          background: #f1f5f9;
          padding: 0.25rem;
          border-radius: 12px;
        }

        .view-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          color: #64748b;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 1.1rem;
        }

        .view-btn:hover {
          color: #255853;
        }

        .view-btn.active {
          background: white;
          color: #255853;
          box-shadow: 0 2px 8px rgba(37, 88, 83, 0.1);
        }

        /* Grid View Cards */
        .content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 2rem;
          animation: fadeIn 0.5s ease;
        }

        .content-card {
          background: white;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(37, 88, 83, 0.1);
          animation: slideUp 0.5s ease forwards;
          opacity: 0;
          transform: translateY(20px);
        }

        .content-card:hover {
          transform: translateY(-6px) scale(1.02);
          box-shadow: 0 20px 30px rgba(37, 88, 83, 0.1);
          border-color: #255853;
        }

        .card-media {
          position: relative;
          height: 200px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f0f0;
        }

        .card-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: transform 0.5s ease;
          background: #f0f0f0;
        }

        .content-card:hover .card-image {
          transform: scale(1.1);
        }

        .media-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          backdrop-filter: blur(2px);
        }

        .content-card:hover .media-overlay {
          opacity: 1;
        }

        .play-button {
          width: 60px;
          height: 60px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #255853;
          font-size: 1.5rem;
          transform: scale(0.8);
          transition: all 0.3s ease;
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
        }

        .content-card:hover .play-button {
          transform: scale(1);
        }

        .media-badge {
          position: absolute;
          top: 15px;
          right: 15px;
          background: rgba(255, 255, 255, 0.95);
          color: #255853;
          padding: 0.5rem 1rem;
          border-radius: 40px;
          font-size: 0.8rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          backdrop-filter: blur(4px);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .no-image-media {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 3rem;
        }

        .card-content {
          padding: 1.5rem;
        }

        .card-category {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0.4rem 1rem;
          background: rgba(37, 88, 83, 0.1);
          color: #255853;
          border-radius: 40px;
          font-size: 0.8rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }

        .card-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.75rem;
          line-height: 1.6;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-description {
          font-size: 0.9rem;
          color: #64748b;
          line-height: 1.8;
          margin-bottom: 1.25rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-author {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #4a5568;
          font-size: 0.85rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .card-date {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          margin-right: auto;
          color: #64748b;
        }

        .card-stats {
          display: flex;
          gap: 1.5rem;
          padding: 1rem 0;
          border-top: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          margin-bottom: 1rem;
        }

        .stat-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #64748b;
          font-size: 0.85rem;
        }

        .card-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .card-button {
          flex: 1;
          background: linear-gradient(135deg, #255853, #357E77);
          color: white;
          border: none;
          padding: 0.75rem 1rem;
          border-radius: 16px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .card-button:hover {
          background: linear-gradient(135deg, #1f4a45, #2d6b64);
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(37, 88, 83, 0.2);
        }

        .card-badges {
          display: flex;
          gap: 0.5rem;
        }

        .badge-card {
          width: 36px;
          height: 36px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.9rem;
        }

        .badge-card.breaking {
          background: #dc2626;
        }

        .badge-card.featured {
          background: #f59e0b;
        }

        /* List View */
        .content-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          animation: fadeIn 0.5s ease;
        }

        .content-card-list {
          background: white;
          border-radius: 20px;
          padding: 1.25rem;
          display: flex;
          gap: 1.5rem;
          border: 1px solid rgba(37, 88, 83, 0.1);
          transition: all 0.3s ease;
          position: relative;
          animation: slideRight 0.5s ease forwards;
          opacity: 0;
          transform: translateX(-20px);
        }

        .content-card-list:hover {
          transform: translateX(-4px);
          box-shadow: 0 10px 25px rgba(37, 88, 83, 0.1);
          border-color: #255853;
        }

        .card-list-image {
          width: 160px;
          height: 120px;
          border-radius: 16px;
          overflow: hidden;
          position: relative;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f0f0;
        }

        .card-list-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #f0f0f0;
        }

        .list-play-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #255853;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
        }

        .no-image-list {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 2rem;
        }

        .card-list-content {
          flex: 1;
          min-width: 0;
        }

        .card-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .card-list-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-list-type {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 0.25rem 0.75rem;
          background: rgba(37, 88, 83, 0.1);
          color: #255853;
          border-radius: 40px;
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .card-list-description {
          font-size: 0.9rem;
          color: #64748b;
          margin-bottom: 0.75rem;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-list-meta {
          display: flex;
          gap: 1.5rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .card-list-meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #4a5568;
          font-size: 0.8rem;
        }

        .card-list-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .card-list-stats {
          display: flex;
          gap: 1rem;
        }

        .card-list-button {
          padding: 0.5rem 1.25rem;
          background: #255853;
          color: white;
          border-radius: 40px;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .card-list-button:hover {
          background: #1f4a45;
          transform: translateX(-2px);
        }

        .card-list-badges {
          position: absolute;
          top: 1rem;
          left: 1rem;
          display: flex;
          gap: 0.5rem;
        }

        .badge-list {
          padding: 0.25rem 0.75rem;
          border-radius: 40px;
          font-size: 0.7rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          color: white;
        }

        .badge-list.breaking {
          background: #dc2626;
        }

        .badge-list.featured {
          background: #f59e0b;
        }

        /* Compact View */
        .content-compact {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
          animation: fadeIn 0.5s ease;
        }

        .content-card-compact {
          background: white;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(37, 88, 83, 0.1);
          transition: all 0.3s ease;
          display: flex;
          gap: 1rem;
          padding: 0.75rem;
          position: relative;
          animation: slideUp 0.5s ease forwards;
          opacity: 0;
          transform: translateY(20px);
        }

        .content-card-compact:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(37, 88, 83, 0.1);
          border-color: #255853;
        }

        .compact-image {
          width: 80px;
          height: 80px;
          border-radius: 14px;
          overflow: hidden;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f0f0;
        }

        .compact-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: #f0f0f0;
        }

        .no-image-compact {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
        }

        .compact-content {
          flex: 1;
          min-width: 0;
        }

        .compact-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.25rem;
        }

        .compact-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .compact-type {
          color: #255853;
          font-size: 0.8rem;
        }

        .compact-meta {
          font-size: 0.7rem;
          color: #64748b;
          margin-bottom: 0.5rem;
          display: flex;
          gap: 0.5rem;
        }

        .compact-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .compact-stats {
          display: flex;
          gap: 0.75rem;
          font-size: 0.7rem;
          color: #64748b;
        }

        .compact-stats span {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .compact-button {
          width: 28px;
          height: 28px;
          background: rgba(37, 88, 83, 0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #255853;
          transition: all 0.3s ease;
        }

        .compact-button:hover {
          background: #255853;
          color: white;
        }

        .compact-badge {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
        }

        .breaking-icon {
          color: #dc2626;
          font-size: 0.9rem;
        }

        .featured-icon {
          color: #f59e0b;
          font-size: 0.9rem;
        }

        /* Loading States */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 5rem;
          min-height: 400px;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 30px;
          margin: 2rem 0;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          color: #cbd5e0;
        }

        .empty-text {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #2d3748;
        }

        .empty-subtext {
          font-size: 0.95rem;
          color: #a0aec0;
        }

        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideRight {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .home {
            padding: 1rem;
          }
          
          .content-card-list {
            flex-direction: column;
          }
          
          .card-list-image {
            width: 100%;
            height: 160px;
          }
          
          .card-list-meta {
            gap: 1rem;
          }
          
          .card-list-footer {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }
          
          .card-list-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      {/* Main Content */}
      <main className="main-container">
        {/* Page Title */}
        <div className="page-title">
          <h1>معرض الصور</h1>
          <p>مجموعة من أجمل الصور والمحتوى البصري</p>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-header" onClick={() => setShowFilters(!showFilters)}>
            <div className="filters-title">
              <FaFilter />
              <span>خيارات البحث والتصفية</span>
            </div>
            <FaChevronDown className={`filters-toggle ${showFilters ? 'open' : ''}`} />
          </div>

          <div className="filters-grid">
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="ابحث في الصور..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="filter-group">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="all">جميع التصنيفات</option>
                {categories.filter(cat => cat !== 'all').map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <FaChevronDown className="select-arrow" />
            </div>
            
            {(searchTerm || selectedCategory !== 'all') && (
              <button
                onClick={handleClearFilters}
                className="clear-filters"
              >
                <FaTimes />
                مسح الفلاتر
              </button>
            )}
          </div>
        </div>

        {/* Results Bar */}
        <div className="results-bar">
          <div className="results-count">
            {filteredContent.length} صورة
          </div>
          
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FaThLarge />
            </button>
            <button 
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <FaList />
            </button>
            <button 
              className={`view-btn ${viewMode === 'compact' ? 'active' : ''}`}
              onClick={() => setViewMode('compact')}
            >
              <FaSquare />
            </button>
          </div>
        </div>

        {/* Content Grid/List */}
        {loading ? (
          <div className="loading-container">
            <Spinner animation="border" variant="primary" />
            <span style={{marginTop: '1.5rem', color: '#255853', fontSize: '1.1rem'}}>
              جاري تحميل الصور...
            </span>
          </div>
        ) : filteredContent.length > 0 ? (
          <>
            {viewMode === 'grid' && (
              <div className="content-grid">
                {filteredContent.map((item, index) => renderCard(item, index))}
              </div>
            )}
            
            {viewMode === 'list' && (
              <div className="content-list">
                {filteredContent.map((item, index) => renderCard(item, index))}
              </div>
            )}
            
            {viewMode === 'compact' && (
              <div className="content-compact">
                {filteredContent.map((item, index) => renderCard(item, index))}
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaImage />
            </div>
            <p className="empty-text">
              لا توجد صور حالياً
            </p>
            {(searchTerm || selectedCategory !== 'all') && (
              <p className="empty-subtext">
                جرب تغيير معايير البحث أو مسح الفلاتر
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Home;